/**
 * Scroll trace for QA: a rolling log of everything that moves the page.
 *
 * Switched on with `?scrolltrace=1` in the URL or `localStorage.tixu_scroll_trace = "1"`; stays on
 * for the tab. It records scroll events (with the distance per event, so a jump stands out from a
 * smooth scroll), every programmatic scroll call with its call stack, focus changes and document
 * height changes. `window.__scrollTrace.dump()` prints the log and copies it as JSON to the
 * clipboard; `window.__scrollTrace.clear()` empties it.
 */

type TraceEntry = {
    t: number;
    kind: "scroll" | "call" | "focus" | "height" | "note";
    detail: string;
    y: number;
    stack?: string;
};

const MAX_ENTRIES = 600;
const FLAG_KEY = "tixu_scroll_trace";

let installed = false;

const now = () => Math.round(performance.now());

const describe = (el: Element | null) => {
    if (!el) return "-";
    const id = el.id ? `#${el.id}` : "";
    const cls = typeof el.className === "string" ? el.className.split(" ").slice(0, 3).join(".") : "";
    return `${el.tagName.toLowerCase()}${id}${cls ? `.${cls}` : ""}`;
};

const shortStack = () =>
    (new Error().stack ?? "")
        .split("\n")
        .slice(3, 7)
        .map((line) =>
            line
                .trim()
                .replace(/^at /, "")
                .replace(/https?:\/\/[^/]+\/_next\/static\/chunks\//g, "")
        )
        .join(" < ")
        .slice(0, 300);

export const isScrollTraceEnabled = () => {
    if (typeof window === "undefined") return false;
    try {
        if (new URLSearchParams(window.location.search).get("scrolltrace") === "1") {
            localStorage.setItem(FLAG_KEY, "1");
        }
        return localStorage.getItem(FLAG_KEY) === "1";
    } catch {
        return false;
    }
};

export function installScrollTrace() {
    if (installed || typeof window === "undefined" || !isScrollTraceEnabled()) return;
    installed = true;

    const entries: TraceEntry[] = [];
    const push = (entry: TraceEntry) => {
        entries.push(entry);
        if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
    };
    const note = (detail: string) => push({ t: now(), kind: "note", detail, y: Math.round(window.scrollY) });

    // Programmatic scrolls, with who asked for them.
    const wrap = (target: object, name: string, label: string) => {
        const original = (target as Record<string, unknown>)[name] as ((...args: unknown[]) => unknown) | undefined;
        if (typeof original !== "function") return;
        (target as Record<string, unknown>)[name] = function (this: unknown, ...args: unknown[]) {
            push({
                t: now(),
                kind: "call",
                detail: `${label}(${JSON.stringify(args).slice(0, 80)})`,
                y: Math.round(window.scrollY),
                stack: shortStack()
            });
            return original.apply(this, args);
        };
    };
    wrap(window, "scrollTo", "window.scrollTo");
    wrap(window, "scrollBy", "window.scrollBy");
    wrap(window, "scroll", "window.scroll");
    wrap(Element.prototype, "scrollIntoView", "scrollIntoView");

    // Scroll events: the distance since the previous event tells a jump from a smooth scroll.
    let lastY = Math.round(window.scrollY);
    window.addEventListener(
        "scroll",
        () => {
            const y = Math.round(window.scrollY);
            push({ t: now(), kind: "scroll", detail: `${lastY} -> ${y} (${y - lastY > 0 ? "+" : ""}${y - lastY})`, y });
            lastY = y;
        },
        { passive: true }
    );

    document.addEventListener("focusin", (event) => {
        const el = event.target as Element | null;
        const rect = el?.getBoundingClientRect();
        push({
            t: now(),
            kind: "focus",
            detail: `${describe(el)} top=${rect ? Math.round(rect.top) : "?"}`,
            y: Math.round(window.scrollY)
        });
    });

    // Document height changes (content appearing, spacers, bubbles).
    let lastHeight = document.documentElement.scrollHeight;
    const observer = new ResizeObserver(() => {
        const height = document.documentElement.scrollHeight;
        if (height !== lastHeight) {
            push({ t: now(), kind: "height", detail: `${lastHeight} -> ${height}`, y: Math.round(window.scrollY) });
            lastHeight = height;
        }
    });
    observer.observe(document.body);

    (window as unknown as { __scrollTrace: unknown }).__scrollTrace = {
        entries,
        note,
        clear: () => entries.splice(0, entries.length),
        dump: () => {
            const json = JSON.stringify(entries, null, 1);
            console.log(
                "[scroll trace]\n" +
                    entries
                        .map((e) => `${e.t} ${e.kind} y=${e.y} ${e.detail}${e.stack ? "  @ " + e.stack : ""}`)
                        .join("\n")
            );
            navigator.clipboard?.writeText(json).catch(() => undefined);
            return json;
        }
    };
    note(`scroll trace on; viewport ${window.innerWidth}x${window.innerHeight}; ${navigator.userAgent}`);
}
